// The creative-test agent's decisions (#1152): judge, simulate, rotate, fill.
// Pure: no Deno, no network, so vitest imports it and index.ts only does I/O.
//
// Real verdicts need an ad (meta_ad_id + slot_start, set by the live executor
// when the ad goes ACTIVE). Shadow rows have neither, so they get a simulated
// verdict on an organic stand-in instead, recorded in the run and in the
// row's reason, never its status.
import {
  BUDGET_SERVICE_LABELS,
  BUDGET_SERVICE_PLAN_PARAM,
  BUDGET_SERVICES,
  PRIMARY_SERVICE_LABELS,
  type BudgetService,
} from "../social-enrich/services.ts";
import {
  comparePoolEntries,
  comparePoolRank,
  poolExclusion,
  scorePercentile,
  type ExcludedPoolEntry,
  type PoolEntry,
} from "../social-enrich/creative-tests.ts";
import type { SocialTags } from "../social-enrich/tags.ts";
import { localDate } from "../lead-flow-monitor/monitor.ts";
import type { AgentSettings } from "./settings.ts";

export const TIMEZONE = "America/Toronto";
export const ROTATION_DAYS = 7;
export const FREQUENCY_STOP = 5;
export const PLAN_URL = "https://app.tristanoscatering.ca/plan";
const DAY_MS = 86_400_000;

export type DecisionKind =
  | "add"
  | "win"
  | "extend"
  | "lose"
  | "wait"
  | "simulated_win"
  | "simulated_extend"
  | "simulated_lose"
  | "simulated_wait"
  | "reserved_empty"
  | "requalify_drop"
  | "fill_deferred"
  | "rotation"
  | "note"
  | "meta_write";

export interface Decision {
  kind: DecisionKind;
  reason: string;
  test_id?: string;
  social_post_id?: string;
  service?: BudgetService;
  slot?: "reserved" | "flex";
  simulated?: true;
  metrics?: Record<string, number | null>;
  /** meta_write only (#1325): the meta-ads call and its outcome. */
  meta?: MetaWriteOutcome;
}

export interface MetaWriteOutcome {
  action: string;
  ok: boolean;
  refused?: string[];
  error?: string;
  status?: number;
  ad_id?: string;
  adset_id?: string;
  campaign_id?: string;
}

/** A creative_tests row holding a slot. */
export interface ActiveTest {
  id: string;
  social_post_id: string;
  service: BudgetService;
  status: "shadow" | "testing" | "extended";
  meta_ad_id: string | null;
  slot_start: string | null;
  created_at: string;
}

/** Cumulative ad metrics since slot_start. */
export interface AdMetrics {
  impressions: number;
  spendCents: number;
  ctr: number | null;
  landingPageViews: number;
  costPerPlanVisit: number | null;
  hookRate: number | null;
  engagement: number | null;
  frequency: number | null;
  realLeads: number;
}

export type MetricsRead = { ok: true; metrics: AdMetrics } | { ok: false; error: string };

export interface OrganicStats {
  engagement_rate: number;
  score: number;
  reach: number;
}

/** The quote form an ad for this service links to. */
export function planLinkForService(service: BudgetService): string {
  return `${PLAN_URL}?service=${BUDGET_SERVICE_PLAN_PARAM[service]}`;
}

/** Whole calendar days in Toronto from `fromMs` to `toMs` (DST-safe). */
export function torontoDaysBetween(fromMs: number, toMs: number): number {
  const day = (ms: number) => Date.parse(`${localDate(ms, TIMEZONE)}T00:00:00Z`);
  return Math.round((day(toMs) - day(fromMs)) / DAY_MS);
}

/** Top / middle / bottom sizes for a cohort of n. */
export function thirds(n: number): { top: number; middle: number; bottom: number } {
  if (n <= 0) return { top: 0, middle: 0, bottom: 0 };
  const top = Math.max(1, Math.round(n / 3));
  const bottom = Math.min(n - top, Math.round(n / 3));
  return { top, middle: n - top - bottom, bottom };
}

/**
 * The verdict for rank `i` (0-based) of `n` in a judged cohort: top third with
 * ≥1 /plan visit or real lead wins; bottom third loses; an extended test that
 * isn't a winner is out; the rest extend. judgeSlots uses it, and so does
 * /admin/social's live standing (#1561), so the two can't drift.
 */
export function cohortVerdict(
  i: number,
  n: number,
  m: Pick<AdMetrics, "landingPageViews" | "realLeads">,
  status: ActiveTest["status"],
): "win" | "lose" | "extend" {
  const { top, bottom } = thirds(n);
  if (i < top && (m.landingPageViews >= 1 || m.realLeads >= 1)) return "win";
  if (i >= n - bottom) return "lose";
  return status === "extended" ? "lose" : "extend";
}

function nullsLast(a: number | null, b: number | null, dir: 1 | -1): number {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return (a - b) * dir;
}

/** Ad cohort order: cost per /plan visit ascending, then CTR descending (nulls last), then id. */
export function rankCohort<T extends { id: string; metrics: AdMetrics }>(cohort: readonly T[]): T[] {
  return [...cohort].sort(
    (a, b) =>
      nullsLast(a.metrics.costPerPlanVisit, b.metrics.costPerPlanVisit, 1) ||
      nullsLast(a.metrics.ctr, b.metrics.ctr, -1) ||
      a.id.localeCompare(b.id),
  );
}

/** Practice-mode order: organic engagement rate, then score, both descending; then id (#1561 shares it). */
export function compareOrganic(
  a: { id: string; stats: Pick<OrganicStats, "engagement_rate" | "score"> },
  b: { id: string; stats: Pick<OrganicStats, "engagement_rate" | "score"> },
): number {
  return b.stats.engagement_rate - a.stats.engagement_rate || b.stats.score - a.stats.score || a.id.localeCompare(b.id);
}

const fmt = (n: number) => n.toLocaleString("en-CA");
const money = (cents: number | null) => (cents === null ? "n/a" : `$${(cents / 100).toFixed(2)}`);
const pct = (v: number | null) => (v === null ? "n/a" : `${(v * 100).toFixed(2)}%`);

function metricsRecord(m: AdMetrics): Record<string, number | null> {
  return {
    impressions: m.impressions,
    spend_cents: m.spendCents,
    ctr: m.ctr,
    landing_page_views: m.landingPageViews,
    cost_per_plan_visit_cents: m.costPerPlanVisit,
    frequency: m.frequency,
    real_leads: m.realLeads,
  };
}

/** A shadow row: no ad has run for it, so no real verdict is possible. */
export function isShadowRow(test: Pick<ActiveTest, "meta_ad_id" | "slot_start">): boolean {
  return !test.meta_ad_id || !test.slot_start;
}

export interface JudgeInput {
  tests: readonly ActiveTest[];
  metrics: ReadonlyMap<string, MetricsRead>;
  settings: Pick<AgentSettings, "judgeAfterImpressions" | "judgeAfterDays">;
  nowMs: number;
}

/**
 * Real verdicts for tests with a running ad. Per test, in order: metrics read
 * failed → wait; frequency ≥ 5 → lose at once; thresholds not met → wait;
 * extended and not yet at 2× the days → wait; otherwise ranked by thirds.
 * Top third with ≥1 /plan visit or real lead wins; without, it counts as middle.
 * Middle extends once (an extended test is out); bottom loses. Real leads are
 * SPA-counted (#1283); Meta's Lead count is never read.
 */
export function judgeSlots(input: JudgeInput): Decision[] {
  const { judgeAfterImpressions: minImp, judgeAfterDays: minDays } = input.settings;
  const decisions: Decision[] = [];
  const cohort: Array<{ id: string; test: ActiveTest; metrics: AdMetrics }> = [];
  for (const test of input.tests) {
    if (isShadowRow(test)) continue;
    const base = { test_id: test.id, social_post_id: test.social_post_id, service: test.service };
    const read = input.metrics.get(test.id);
    if (!read || !read.ok) {
      decisions.push({ ...base, kind: "wait", reason: `No verdict: metrics couldn't be read (${read ? read.error : "no read"}).` });
      continue;
    }
    const m = read.metrics;
    if (m.frequency !== null && m.frequency >= FREQUENCY_STOP) {
      decisions.push({
        ...base,
        kind: "lose",
        metrics: metricsRecord(m),
        reason: `Frequency ${m.frequency.toFixed(1)} ≥ ${FREQUENCY_STOP}: hard stop; pause ad ${test.meta_ad_id}.`,
      });
      continue;
    }
    const days = Math.floor((input.nowMs - Date.parse(test.slot_start as string)) / DAY_MS);
    if (m.impressions < minImp || days < minDays) {
      decisions.push({
        ...base,
        kind: "wait",
        metrics: metricsRecord(m),
        reason: `Not judged yet: ${fmt(m.impressions)} of ${fmt(minImp)} impressions, day ${days} of ${minDays}.`,
      });
      continue;
    }
    if (test.status === "extended" && days < 2 * minDays) {
      decisions.push({
        ...base,
        kind: "wait",
        metrics: metricsRecord(m),
        reason: `Extended: re-judged on day ${2 * minDays} (now day ${days}).`,
      });
      continue;
    }
    cohort.push({ id: test.id, test, metrics: m });
  }

  const ranked = rankCohort(cohort);
  const { top, bottom } = thirds(ranked.length);
  ranked.forEach(({ test, metrics: m }, i) => {
    const base = { test_id: test.id, social_post_id: test.social_post_id, service: test.service, metrics: metricsRecord(m) };
    const stats = `rank ${i + 1} of ${ranked.length}, ${money(m.costPerPlanVisit)} per /plan visit, CTR ${pct(m.ctr)}, ${m.landingPageViews} /plan visits, ${m.realLeads} real leads`;
    const inTop = i < top;
    const inBottom = i >= ranked.length - bottom;
    const verdict = cohortVerdict(i, ranked.length, m, test.status);
    if (verdict === "win") {
      decisions.push({
        ...base,
        kind: "win",
        reason: `Winner: top third (${stats}). Queue for graduation into ${BUDGET_SERVICE_LABELS[test.service]} (#976).`,
      });
    } else if (inBottom) {
      decisions.push({ ...base, kind: "lose", reason: `Loser: bottom third (${stats}).` });
    } else if (verdict === "lose") {
      decisions.push({ ...base, kind: "lose", reason: `Extended once and still not a winner at re-judge: out (${stats}).` });
    } else {
      const why = inTop ? "top third but no /plan visit or real lead yet" : "middle third";
      decisions.push({ ...base, kind: "extend", reason: `Extended one more week: ${why} (${stats}).` });
    }
  });
  return decisions;
}

export interface SimulateInput {
  tests: readonly ActiveTest[];
  organic: ReadonlyMap<string, OrganicStats>;
  settings: Pick<AgentSettings, "judgeAfterDays">;
  nowMs: number;
}

export const SIMULATED_PREFIX = "Simulated (organic stand-in, not an ad result):";

/**
 * Simulated verdicts for shadow rows, so the shadow week shows what the judge
 * would crown or cut. Same thirds as the real judge, on the post's organic
 * engagement rate (standing in for cost per /plan visit). Eligible from Toronto
 * day `judgeAfterDays` after the proposal. Decided by age alone, so it never
 * depends on an earlier run: middle third extends up to day 2N and is cut from
 * day 2N. Recorded in the run and appended to the row's reason
 * (reasonWithSimulated); never changes the row's status, so it never feeds
 * results or graduation.
 */
export function simulateShadowVerdicts(input: SimulateInput): Decision[] {
  const n = input.settings.judgeAfterDays;
  const decisions: Decision[] = [];
  const cohort: Array<{ test: ActiveTest; stats: OrganicStats; days: number }> = [];
  for (const test of input.tests) {
    if (!isShadowRow(test)) continue;
    const base = { test_id: test.id, social_post_id: test.social_post_id, service: test.service, simulated: true as const };
    const days = torontoDaysBetween(Date.parse(test.created_at), input.nowMs);
    const stats = input.organic.get(test.social_post_id);
    if (days < n) {
      decisions.push({ ...base, kind: "simulated_wait", reason: `${SIMULATED_PREFIX} day ${days} of ${n}; judged from day ${n}.` });
    } else if (!stats) {
      decisions.push({ ...base, kind: "simulated_wait", reason: `${SIMULATED_PREFIX} no current organic metrics for this post (older than 90 days or below the reach floor).` });
    } else {
      cohort.push({ test, stats, days });
    }
  }
  cohort.sort((a, b) => compareOrganic({ id: a.test.id, stats: a.stats }, { id: b.test.id, stats: b.stats }));
  const { top, bottom } = thirds(cohort.length);
  cohort.forEach(({ test, stats, days }, i) => {
    const base = { test_id: test.id, social_post_id: test.social_post_id, service: test.service, simulated: true as const };
    const line = `rank ${i + 1} of ${cohort.length}, engagement rate ${pct(stats.engagement_rate)}, reach ${fmt(stats.reach)}, day ${days}`;
    if (i < top) {
      decisions.push({ ...base, kind: "simulated_win", reason: `${SIMULATED_PREFIX} would crown: top third (${line}).` });
    } else if (i >= cohort.length - bottom) {
      decisions.push({ ...base, kind: "simulated_lose", reason: `${SIMULATED_PREFIX} would cut: bottom third (${line}).` });
    } else if (days < 2 * n) {
      decisions.push({ ...base, kind: "simulated_extend", reason: `${SIMULATED_PREFIX} would extend one more week: middle third (${line}).` });
    } else {
      decisions.push({ ...base, kind: "simulated_lose", reason: `${SIMULATED_PREFIX} would cut: still middle third after the extension (${line}).` });
    }
  });
  return decisions;
}

export const SIMULATED_REASON_MARKER = "\nSimulated verdict";

/**
 * A shadow row's reason with its latest simulated verdict: the proposal reason,
 * then one "Simulated verdict (<date>): …" line, replacing any earlier one.
 */
export function reasonWithSimulated(current: string | null, verdictReason: string, date: string): string {
  const base = (current ?? "").split(SIMULATED_REASON_MARKER)[0];
  return `${base}${SIMULATED_REASON_MARKER} (${date}): ${verdictReason}`;
}

/**
 * Rotation is weekly (judging is daily). Due when nothing is active, there has
 * never been a rotation, or ≥7 Toronto calendar days have passed since the last
 * run that rotated (whether or not it added anything).
 */
export function rotationDue(lastRotationAt: string | null, activeCount: number, nowMs: number): boolean {
  if (activeCount === 0 || lastRotationAt === null) return true;
  return torontoDaysBetween(Date.parse(lastRotationAt), nowMs) >= ROTATION_DAYS;
}

/** The Toronto date of the next rotation, for fill_deferred reasons. */
export function nextRotationDate(lastRotationAt: string): string {
  return localDate(Date.parse(lastRotationAt) + ROTATION_DAYS * DAY_MS, TIMEZONE);
}

export interface Placement {
  entry: PoolEntry;
  slot: "reserved" | "flex";
}

export interface FillInput {
  /** Tests still holding a slot after this run's real verdicts. */
  active: ReadonlyArray<Pick<ActiveTest, "service" | "social_post_id">>;
  pools: Readonly<Record<BudgetService, readonly PoolEntry[]>>;
  /** Posts each pool left out (rankTestPools), named in the reasons. */
  excluded?: Readonly<Record<BudgetService, readonly ExcludedPoolEntry[]>>;
  slots: number;
  /**
   * A refill between rotations (#1402): add at most `count` posts, and match
   * reserved slots only for the dropped picks' services. Absent on a
   * rotation, which fills every empty slot.
   */
  refill?: { count: number; services: readonly BudgetService[] };
}

/** How many skipped posts a reason names before "+N more". */
const SKIPPED_SHOWN = 3;

const compareFlexFit = comparePoolEntries<PoolEntry>((entry) => entry.primary_service === entry.service);

function compareFlex(a: PoolEntry, b: PoolEntry): number {
  return compareFlexFit(a, b) || BUDGET_SERVICES.indexOf(a.service) - BUDGET_SERVICES.indexOf(b.service);
}

const shortId = (id: string) => id.slice(0, 8);

/** " Skipped above it: <id> (excluded: <why>), …" for left-out posts that outrank the pick, or "". */
function skippedText(entry: PoolEntry, excluded: FillInput["excluded"]): string {
  const rank = comparePoolRank(entry.service);
  const above = (excluded?.[entry.service] ?? []).filter((e) => rank(e, entry) < 0);
  if (above.length === 0) return "";
  const named = above.slice(0, SKIPPED_SHOWN).map((e) => `${shortId(e.social_post_id)} (excluded: ${e.exclusion})`);
  const more = above.length > SKIPPED_SHOWN ? `, +${above.length - SKIPPED_SHOWN} more` : "";
  return ` Skipped above it: ${named.join(", ")}${more}.`;
}

function addReason(p: Placement, input: FillInput): string {
  const { entry } = p;
  const label = BUDGET_SERVICE_LABELS[entry.service];
  return (
    `${p.slot === "reserved" ? "Reserved" : "Flex"} ${label} slot: pool rank ${entry.rank} of ${input.pools[entry.service].length}, ` +
    `score percentile ${scorePercentile(entry.score)}, ${entry.content_type}, occasion ${entry.occasion}. ` +
    `Its ad would link to ${planLinkForService(entry.service)}.` +
    skippedText(entry, input.excluded)
  );
}

/** " (N left out: giveaway, dated post)" for a service whose every post was excluded, or "". */
function leftOutText(service: BudgetService, excluded: FillInput["excluded"]): string {
  const left = excluded?.[service] ?? [];
  if (left.length === 0) return "";
  return ` (${left.length} left out: ${[...new Set(left.map((e) => e.exclusion))].join(", ")})`;
}

/**
 * Fills empty slots. Each budget service holds one reserved slot: services
 * with no active test are matched to unused posts by augmenting-path bipartite
 * matching, scarcest service first, so the number of services covered is the
 * maximum any assignment reaches (a post tagged wedding + event_space that is
 * the only event_space post goes to event_space). A reserved slot with no
 * eligible post stays empty. The other `slots − 5` are flex: the best unused
 * post across all pools. A post is placed once, only for a service in its
 * `services`. Nothing is evicted when `slots` was lowered.
 */
export function fillSlots(input: FillInput): { placements: Placement[]; decisions: Decision[] } {
  const { pools, slots, refill } = input;
  const held = Object.fromEntries(BUDGET_SERVICES.map((s) => [s, 0])) as Record<BudgetService, number>;
  for (const t of input.active) held[t.service] += 1;
  let total = input.active.length;
  const limit = refill ? Math.min(slots, total + refill.count) : slots;
  const used = new Set(input.active.map((t) => t.social_post_id));
  const placements: Placement[] = [];
  const decisions: Decision[] = [];

  if (total > slots) {
    decisions.push({ kind: "note", reason: `${total} tests hold slots but slots is ${slots}: nothing evicted, nothing added.` });
    return { placements, decisions };
  }

  // Reserved: maximum matching of uncovered services to unused posts.
  const open = (s: BudgetService) => pools[s].filter((e) => !used.has(e.social_post_id));
  const uncovered = BUDGET_SERVICES.filter((s) => held[s] === 0);
  // A refill matches only the dropped picks' services, so the post that
  // justified a drop can't be routed to a slot left empty since the rotation.
  const needing = uncovered.filter((s) => !refill || refill.services.includes(s)).sort(
    (a, b) => open(a).length - open(b).length || BUDGET_SERVICES.indexOf(a) - BUDGET_SERVICES.indexOf(b),
  );
  const untilRotation = (s: BudgetService) =>
    `Reserved ${BUDGET_SERVICE_LABELS[s]} slot left empty until the next rotation: this run only refills dropped picks.`;
  for (const s of uncovered) {
    if (!needing.includes(s)) decisions.push({ kind: "reserved_empty", service: s, reason: untilRotation(s) });
  }
  const byPost = new Map<string, BudgetService>();
  const byService = new Map<BudgetService, PoolEntry>();
  const augment = (s: BudgetService, seen: Set<string>): boolean => {
    for (const entry of open(s)) {
      if (seen.has(entry.social_post_id)) continue;
      seen.add(entry.social_post_id);
      const owner = byPost.get(entry.social_post_id);
      if (owner === undefined || augment(owner, seen)) {
        byPost.set(entry.social_post_id, s);
        byService.set(s, entry);
        return true;
      }
    }
    return false;
  };
  for (const s of needing) augment(s, new Set());
  for (const s of needing) {
    const entry = byService.get(s);
    if (entry && total < limit) {
      placements.push({ entry, slot: "reserved" });
      used.add(entry.social_post_id);
      held[s] += 1;
      total += 1;
    } else {
      decisions.push({
        kind: "reserved_empty",
        service: s,
        reason: entry
          ? total < slots
            ? untilRotation(s)
            : `Reserved ${BUDGET_SERVICE_LABELS[s]} slot left empty: no free slot (${total} of ${slots} held).`
          : `Reserved ${BUDGET_SERVICE_LABELS[s]} slot left empty: no eligible posts${leftOutText(s, input.excluded)}.`,
      });
    }
  }

  // Flex: whatever is left above one per service.
  const flexCapacity = slots - BUDGET_SERVICES.length;
  const flexUsed = () => BUDGET_SERVICES.reduce((n, s) => n + Math.max(0, held[s] - 1), 0);
  while (total < limit && flexUsed() < flexCapacity) {
    const best = BUDGET_SERVICES.flatMap((s) => open(s))
      .filter((e) => !used.has(e.social_post_id))
      .sort(compareFlex)[0];
    if (!best) break;
    placements.push({ entry: best, slot: "flex" });
    used.add(best.social_post_id);
    held[best.service] += 1;
    total += 1;
  }

  // Reserved placements first in BUDGET_SERVICES order, then flex in pick order.
  placements.sort((a, b) =>
    a.slot === b.slot
      ? a.slot === "reserved"
        ? BUDGET_SERVICES.indexOf(a.entry.service) - BUDGET_SERVICES.indexOf(b.entry.service)
        : 0
      : a.slot === "reserved" ? -1 : 1,
  );
  const adds: Decision[] = placements.map((p) => ({
    kind: "add",
    social_post_id: p.entry.social_post_id,
    service: p.entry.service,
    slot: p.slot,
    reason: addReason(p, input),
  }));
  // A flex refill can land in a service logged above as waiting for the rotation.
  const empties = decisions.filter((d) => !(d.kind === "reserved_empty" && d.service && held[d.service] > 0));
  return { placements, decisions: [...adds, ...empties] };
}

/** A slot-holding test with its post's current caption, media type and tags (null when missing or malformed). */
export interface HeldPost extends Pick<ActiveTest, "id" | "social_post_id" | "service" | "status" | "meta_ad_id" | "slot_start"> {
  caption: string | null;
  media_type: string | null;
  tags: SocialTags | null;
}

/** Why a held pick no longer qualifies for its slot, or null when it still does. */
function disqualified(t: HeldPost, pools: FillInput["pools"]): string | null {
  const why = poolExclusion({ caption: t.caption, content_type: t.tags?.content_type ?? null, media_type: t.media_type });
  if (why !== null) return `excluded: ${why}`;
  if (!t.tags) return null;
  const label = BUDGET_SERVICE_LABELS[t.service];
  if (!t.tags.services.includes(t.service)) {
    return `its tags no longer list ${label} (now ${t.tags.services.map((s) => BUDGET_SERVICE_LABELS[s]).join(", ") || "none"})`;
  }
  const fits = pools[t.service].find((e) => e.primary_service === t.service);
  // A paid test runs to its verdict: the main-service rule drops only shadow picks (#1445).
  if (t.status === "shadow" && t.tags.primary_service !== t.service && fits) {
    return `its main service is ${PRIMARY_SERVICE_LABELS[t.tags.primary_service]}, and the ${label} pool has a post made for ${label}, ${shortId(fits.social_post_id)}`;
  }
  return null;
}

/**
 * Re-checks every held pick against its post's current tags and the pool
 * exclusions (#1402), so a re-tag between rotations doesn't leave an ad on the
 * wrong service's form for a week. A pick is dropped when the pool would now
 * leave its post out, when its tags no longer list the slot's service, or when
 * its main service is now another one and the slot's pool has a post made for
 * the service. That last rule skips ad-backed picks (testing, extended), so a paid
 * test runs to its verdict (#1445). Untagged posts are only checked against the format and caption rules (#1380).
 * The run refills each dropped slot the same day.
 */
export function requalifyHeld(held: readonly HeldPost[], pools: FillInput["pools"]): Decision[] {
  return held.flatMap((t) => {
    const why = disqualified(t, pools);
    if (why === null) return [];
    const pause = t.meta_ad_id ? ` In live mode its ad ${t.meta_ad_id} is paused.` : "";
    return [{
      kind: "requalify_drop" as const,
      test_id: t.id,
      social_post_id: t.social_post_id,
      service: t.service,
      reason:
        `${BUDGET_SERVICE_LABELS[t.service]} slot: dropped ${shortId(t.social_post_id)}, which no longer qualifies (${why}).${pause} ` +
        "This frees the slot for a refill from the pool.",
    }];
  });
}
